import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Boxes, FileText, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GlossyBadge } from "@/components/GlossyBadge";
import welpelLogo from "@/assets/welpel-logo.png";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-white">
      <div className="hidden w-1/2 flex-col justify-between bg-gradient-to-br from-[#172554] via-[#1e3a8a] to-[#1d4ed8] p-12 text-white lg:flex">
        <div className="flex items-center gap-3">
          <img src={welpelLogo} alt="Welpel" className="h-10 w-10 object-contain" data-testid="login-brand-badge" />
          <span className="font-heading text-xl font-bold">Welpel</span>
        </div>
        <div>
          <h1 className="font-heading text-4xl font-bold leading-tight">
            Run your import business from one dashboard.
          </h1>
          <p className="mt-4 max-w-md text-base text-blue-200">
            CRM pipeline, team tasks, warehouse stock and branded quotations — built for
            IndiaMart-first B2B trading.
          </p>
          <div className="mt-10 grid grid-cols-2 gap-4">
            {[
              { icon: Users, t: "Odoo-style CRM", d: "Leads, pipeline & follow-ups", tone: "blue" },
              { icon: Boxes, t: "Warehouse Stock", d: "Inbound, outbound & value", tone: "sky" },
              { icon: FileText, t: "Quotes & Invoices", d: "PDF + WhatsApp sharing", tone: "indigo" },
              { icon: ShieldCheck, t: "SOPs & Tasks", d: "Process for every department", tone: "navy" },
            ].map((f) => (
              <div key={f.t} className="group flex items-center gap-3 rounded-xl border border-white/10 bg-white/10 p-4 backdrop-blur-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-white/15">
                <GlossyBadge icon={f.icon} size={36} tone={f.tone} />
                <div>
                  <div className="text-sm font-semibold">{f.t}</div>
                  <div className="text-xs text-blue-200">{f.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="text-xs text-blue-300">Welpel — B2B operations platform</div>
      </div>

      <div className="flex flex-1 items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <h2 className="font-heading text-3xl font-bold text-slate-900">Welcome back</h2>
          <p className="mt-1 text-sm text-slate-500">Sign in to your workspace</p>
          <div className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="mt-1.5"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5"
                data-testid="login-password-input"
              />
            </div>
            {error && (
              <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600" data-testid="login-error">
                {error}
              </div>
            )}
            <Button
              type="submit"
              className="w-full bg-blue-700 hover:bg-blue-800"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
